import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { EASE, MaskedLines, Reveal } from "@/components/aklive/motion";
import { PlaceholderBlock } from "@/components/aklive/PlaceholderBlock";

const SERVICES = [
  { need: "Launching something?", name: "Brand Identity", outcome: "Build recognition and trust from the beginning.", label: "[BRAND SYSTEM — 4:5]" },
  { need: "Raising or pitching?", name: "Pitch Decks and Presentations", outcome: "Make the opportunity clear and credible.", label: "[PITCH DECK SPREAD — 4:5]" },
  { need: "Building your online presence?", name: "Website Design", outcome: "Help visitors understand the business and take action.", label: "[WEBSITE MOCKUP — 4:5]" },
  { need: "Growing your communication?", name: "Social and Content Systems", outcome: "Create a consistent system your team can use repeatedly.", label: "[CONTENT SYSTEM — 4:5]" },
  { need: "Taking a product to market?", name: "Packaging Design", outcome: "Shape perception before the product is opened.", label: "[PACKAGING CLOSE-UP — 4:5]" },
  { need: "Creating an experience?", name: "Event Branding", outcome: "Build a cohesive environment people remember.", label: "[EVENT BRANDING — 4:5]" },
];

export const ServicesPreview = () => {
  const [active, setActive] = useState(0);

  return (
    <section className="py-24 md:py-40" data-testid="services-preview-section">
      <div className="container-ak">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                <MaskedLines
                  lines={[
                    <span key="l1">
                      Services<span className="text-ember">.</span>
                    </span>,
                  ]}
                />
              </h2>
              <Reveal delay={0.15} className="mt-6">
                <p className="max-w-[420px] text-base leading-relaxed text-ink2 md:text-lg">
                  Design, organized around what your business needs to achieve.
                </p>
              </Reveal>
              <Reveal delay={0.25} className="mt-10 hidden lg:block">
                <div aria-hidden="true" className="relative max-w-[380px]" style={{ aspectRatio: "4 / 5" }}>
                  <AnimatePresence>
                    <motion.div
                      key={active}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="absolute inset-0"
                      data-testid="services-active-visual"
                    >
                      <PlaceholderBlock label={SERVICES[active].label} fill />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <ul className="border-t border-line" data-testid="services-index">
                {SERVICES.map((s, i) => (
                  <li key={s.name} className="border-b border-line">
                    <Link
                      to="/services"
                      data-testid={`service-row-${i}`}
                      onMouseEnter={() => setActive(i)}
                      onFocus={() => setActive(i)}
                      className="group flex min-h-[44px] items-baseline justify-between gap-6 py-6 md:py-8"
                    >
                      <span className="min-w-0">
                        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ember md:text-xs">
                          {s.need}
                        </span>
                        <span className="mt-2 block font-display text-2xl font-bold tracking-tight text-ink transition-transform duration-300 ease-out group-hover:translate-x-2 md:text-3xl lg:text-4xl">
                          {s.name}
                        </span>
                        <span className="mt-2 block max-w-[480px] text-sm leading-relaxed text-ink2 md:text-base">
                          {s.outcome}
                        </span>
                      </span>
                      <span
                        aria-hidden="true"
                        className="self-center text-xl text-ink2 transition-[transform,color] duration-300 group-hover:translate-x-1 group-hover:text-ember md:text-2xl"
                      >
                        →
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-12 md:mt-16">
          <Link to="/services" data-testid="services-explore-btn" className="btn-secondary h-14 px-8 text-base">
            Explore Our Services
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
